export type SummaryGroupingValueRange = {
  min: string | number | null
  max: string | number | null
}

export type SummaryGroupingSettings = {
  current_field_id: string | number | null
  list_id: string | number | null
  optional_field_id: string | number | null
  computed_type_id: string | null
  computed_condition_id: string | null
  condition_value: string | number | null
  condition_value_range: SummaryGroupingValueRange
  condition_value_in_list: Array<string | number> | null
}

export type SummaryGroupingFilterSource = 'current' | 'another-field'

export type SummaryGroupingDraftInput = Partial<Omit<SummaryGroupingSettings, 'condition_value_range'>> & {
  condition_value_range?: Partial<SummaryGroupingValueRange> | null
  filter_source?: SummaryGroupingFilterSource
  placeholder_zero_values?: boolean
}

export type SummaryGroupingDraft = SummaryGroupingSettings & {
  filter_source: SummaryGroupingFilterSource
  placeholder_zero_values: boolean
}

export type SummaryGroupingContext = {
  current_field_id: string | number
  list_id?: string | number | null
}

const SINGLE_VALUE_CONDITIONS = new Set(['MORE', 'LESS', 'EQUAL', 'INCLUDE'])

function normalizeScalar(value: unknown): string | number | null {
  return value === undefined || value === '' ? null : (value as string | number | null)
}

function normalizeString(value: unknown): string | null {
  const normalized = normalizeScalar(value)
  return normalized == null ? null : String(normalized)
}

function normalizeRange(value: SummaryGroupingDraftInput['condition_value_range']): SummaryGroupingValueRange {
  return {
    min: normalizeScalar(value?.min),
    max: normalizeScalar(value?.max),
  }
}

function normalizeList(value: SummaryGroupingDraftInput['condition_value_in_list']) {
  return Array.isArray(value) && value.length > 0 ? value : null
}

function normalizeZeroValue(
  value: string | number | null | undefined,
  placeholderZeroValues: boolean,
): string | number | null {
  if (!placeholderZeroValues) return normalizeScalar(value)
  return value === 0 || value === '0' ? null : normalizeScalar(value)
}

function normalizeRangeValue(
  value: SummaryGroupingDraftInput['condition_value_range'],
  placeholderZeroValues: boolean,
): SummaryGroupingValueRange {
  return {
    min: normalizeZeroValue(value?.min, placeholderZeroValues),
    max: normalizeZeroValue(value?.max, placeholderZeroValues),
  }
}

function hasMeaningfulConditionValue(result: SummaryGroupingSettings): boolean {
  return (
    result.condition_value != null ||
    result.condition_value_range.min != null ||
    result.condition_value_range.max != null ||
    result.condition_value_in_list != null
  )
}

function normalizeFilterSource(
  input: SummaryGroupingDraftInput,
  optionalFieldId: string | number | null,
): SummaryGroupingFilterSource {
  if (input.filter_source) return input.filter_source
  return optionalFieldId == null ? 'current' : 'another-field'
}

function normalizeOptionalFieldId(
  input: SummaryGroupingDraftInput,
  filterSource: SummaryGroupingFilterSource,
): string | number | null {
  if (filterSource === 'current') return null
  return input.optional_field_id ?? null
}

/** Persist field ids as strings so `computed_settings` JSON matches Go `string` tags (numeric ids must not be raw JSON numbers). */
function persistFieldId(value: string | number | null | undefined): string | null {
  if (value === undefined || value === null || value === '') return null
  return String(value)
}

function normalizeCore(input: SummaryGroupingDraftInput, context: SummaryGroupingContext): SummaryGroupingSettings {
  const condition = normalizeScalar(input.computed_condition_id)
  const filterSource = normalizeFilterSource(input, input.optional_field_id ?? null)
  const placeholderZeroValues = Boolean(input.placeholder_zero_values)
  const optionalRaw = normalizeOptionalFieldId(input, filterSource)
  const result: SummaryGroupingSettings = {
    current_field_id: String(context.current_field_id),
    list_id: input.list_id ?? context.list_id ?? null,
    optional_field_id: persistFieldId(optionalRaw),
    computed_type_id: normalizeString(input.computed_type_id),
    computed_condition_id: normalizeString(condition),
    condition_value: normalizeZeroValue(input.condition_value, placeholderZeroValues),
    condition_value_range: normalizeRangeValue(input.condition_value_range, placeholderZeroValues),
    condition_value_in_list: normalizeList(input.condition_value_in_list),
  }

  if (condition == null) {
    return { ...result, condition_value: null, condition_value_range: { min: null, max: null }, condition_value_in_list: null }
  }

  if (placeholderZeroValues && SINGLE_VALUE_CONDITIONS.has(String(condition)) && !hasMeaningfulConditionValue(result)) {
    return {
      ...result,
      computed_condition_id: null,
      condition_value: null,
      condition_value_range: { min: null, max: null },
      condition_value_in_list: null,
    }
  }

  if (placeholderZeroValues && String(condition) === 'RANGE' && !hasMeaningfulConditionValue(result)) {
    return {
      ...result,
      computed_condition_id: null,
      condition_value: null,
      condition_value_range: { min: null, max: null },
      condition_value_in_list: null,
    }
  }

  if (SINGLE_VALUE_CONDITIONS.has(String(condition))) {
    return { ...result, condition_value_range: { min: null, max: null }, condition_value_in_list: null }
  }

  if (String(condition) === 'RANGE') {
    return { ...result, condition_value: null, condition_value_in_list: null }
  }

  if (String(condition) === 'IN_LIST') {
    return { ...result, condition_value: null, condition_value_range: { min: null, max: null } }
  }

  return result
}

export function normalizeSummaryGroupingSettings(
  input: SummaryGroupingDraftInput,
  context: SummaryGroupingContext,
): SummaryGroupingSettings {
  return normalizeCore(input, context)
}

export function buildSummaryGroupingDraft(
  saved: SummaryGroupingDraftInput,
  context: SummaryGroupingContext,
): SummaryGroupingDraft {
  const normalized = normalizeCore(
    {
      current_field_id: context.current_field_id,
      list_id: saved.list_id ?? context.list_id ?? null,
      optional_field_id: saved.optional_field_id ?? null,
      computed_type_id: saved.computed_type_id ?? null,
      computed_condition_id: saved.computed_condition_id ?? null,
      condition_value: saved.condition_value ?? null,
      condition_value_range: saved.condition_value_range ?? { min: null, max: null },
      condition_value_in_list: saved.condition_value_in_list ?? null,
      filter_source: saved.filter_source,
    },
    context,
  )

  return {
    ...normalized,
    filter_source: normalizeFilterSource(saved, normalized.optional_field_id),
    placeholder_zero_values: Boolean(saved.placeholder_zero_values),
  }
}

export function summaryGroupingDraftEquals(
  left: SummaryGroupingDraftInput,
  right: SummaryGroupingDraftInput,
  context: SummaryGroupingContext,
): boolean {
  return JSON.stringify(normalizeCore(left, context)) === JSON.stringify(normalizeCore(right, context))
}

export function toPersistedSummaryGroupingSettings(
  draft: SummaryGroupingDraftInput,
  context: SummaryGroupingContext,
): SummaryGroupingSettings {
  return normalizeCore(draft, context)
}
